// Das Ende eines Runs (M9, Fabi 29.10.2026; Erste Fassung, Runde 5): drei Fakten über die Reise, ein Satz fürs Logbuch,
// den man ändern darf, und der Eintrag für die Bibliothek aller Runs mit Vergleichen. Rein und deterministisch; wo die
// Bibliothek liegt (Browser-Speicher) und wann sie gespeichert wird, entscheidet die Darstellung.
import { biomeOfDay, getEnemy } from '../../content/index.ts';
import type { RunFriend, RunState } from './types.ts';

/** Ein Eintrag der Bibliothek; Datum und Satz setzt die Darstellung */
export type RunRecord = {
  id: string;
  date: string;
  won: boolean;
  days: number;
  fights: number;
  farewells: number;
  driven: number;
  crew: { name: string; species: string; level: number; gone: boolean }[];
  line: string;
};

const everyone = (run: RunState): RunFriend[] => [...run.crew, ...run.gone];
const fightsOf = (run: RunState) => run.chronicle.filter((c) => c.friends?.length).length;
const farewellsOf = (run: RunState) => run.gone.filter((f) => f.gone?.how === 'abschied').length;

function top(list: RunFriend[], value: (f: RunFriend) => number): RunFriend | null {
  const sorted = [...list].filter((f) => value(f) > 0).sort((a, b) => value(b) - value(a) || a.id.localeCompare(b.id));
  return sorted[0] ?? null;
}

/**
 * M9: drei interessante Fakten, zuerst die, die Fabi genannt hat (wer am meisten Schaden gemacht hat, welches
 * Sturmwesen am häufigsten vertrieben wurde, wer am meisten eingesteckt hat); fehlt eins, kommt ein anderes
 */
export function runFacts(run: RunState): string[] {
  const all = everyone(run);
  const out: string[] = [];
  const dmg = top(all, (f) => f.tally.damage);
  if (dmg) out.push(`${dmg.name} hat am meisten ausgeteilt: ${Math.round(dmg.tally.damage)} Schaden in ${dmg.tally.fights} ${dmg.tally.fights === 1 ? 'Kampf' : 'Kämpfen'}.`);
  const driven = Object.entries(run.driven ?? {}).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
  if (driven && driven[1] >= 2) out.push(`Am häufigsten habt ihr ${getEnemy(driven[0]).plural ?? getEnemy(driven[0]).name} vertrieben, ${driven[1]}-mal.`);
  const taken = top(all, (f) => f.tally.taken);
  if (taken) out.push(`${taken.name} hat am meisten eingesteckt: ${Math.round(taken.tally.taken)} Schaden${taken.gone ? '' : ', und ist trotzdem an Bord geblieben'}.`);
  const healed = top(all, (f) => f.tally.healed);
  if (healed && out.length < 3) out.push(`${healed.name} hat am meisten geheilt: ${Math.round(healed.tally.healed)} Leben.`);
  const level = top(all, (f) => f.level);
  if (level && out.length < 3) out.push(`${level.name} hat es bis Level ${level.level} geschafft.`);
  if (out.length < 3) out.push(`Ihr habt ${fightsOf(run)} ${fightsOf(run) === 1 ? 'Kampf' : 'Kämpfe'} ausgefochten.`);
  return out.slice(0, 3);
}

const listNames = (names: string[]) => (names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} und ${names[names.length - 1]}`);

/** M9: Der Vorschlag fürs Logbuch, ein warmer Satz über die Reise; die Spielerin darf ihn ändern */
export function logbookSuggestion(run: RunState): string {
  const aboard = run.crew.filter((f) => !f.gone).map((f) => f.name);
  const lost = run.gone.filter((f) => f.gone?.how === 'abschied').map((f) => f.name);
  const best = top(everyone(run), (f) => f.tally.damage + f.tally.healed + f.tally.taken / 2);
  const k = run.seed % 3;
  const days = ['einem Tag', 'zwei Tagen', 'drei Tagen', 'vier Tagen'][run.day - 1] ?? `${run.day} Tagen`;
  if (run.status === 'won') {
    const missing = lost.length ? ` ${listNames(lost)} ${lost.length === 1 ? 'fehlt' : 'fehlen'} uns sehr.` : ' Alle sind angekommen.';
    const opening = [
      `Mit ${listNames(aboard)} sind wir in ${days} bis ans Meer gefahren.`,
      `Wir haben es geschafft: ${listNames(aboard)} sitzen am milden Meer und schauen dem Sturm hinterher.`,
      `${days.charAt(0).toUpperCase()}${days.slice(1).replace('Tagen', 'Tage').replace('einem Tag', 'ein Tag')} auf dem Fluss, und jetzt riecht es nach Salz.`,
    ][k]!;
    return `${opening}${missing}`;
  }
  const where = biomeOfDay(run.day);
  const fought = best ? ` ${best.name} hat bis zuletzt gekämpft.` : '';
  return [
    `Bis ${where.id === 'moor' ? 'ins Moor' : where.id === 'weinhaenge' ? 'an die Weinhänge' : 'in den Tannenwald'} sind wir gekommen, dann hat uns der Sturm eingeholt.${fought}`,
    `Am ${run.day}. Tag hat uns der Sturm erwischt.${fought} Nächstes Mal fahren wir weiter.`,
    `Der Winter war schneller als wir.${fought}`,
  ][k]!;
}

/** Der Eintrag für die Bibliothek (ohne Datum und mit dem vorgeschlagenen Satz) */
export function runRecord(run: RunState): RunRecord {
  return {
    id: String(run.seed),
    date: '',
    won: run.status === 'won',
    days: run.day,
    fights: fightsOf(run),
    farewells: farewellsOf(run),
    driven: Object.values(run.driven ?? {}).reduce((a, b) => a + b, 0),
    crew: everyone(run).map((f) => ({ name: f.name, species: f.species, level: f.level, gone: !!f.gone })),
    line: logbookSuggestion(run),
  };
}

/** M9: Wie sich dieser Run in die Bibliothek einordnet, etwa „dein längster Run“ oder „so viel gekämpft wie nie“ */
export function compareRuns(rec: RunRecord, earlier: RunRecord[]): string[] {
  const before = earlier.filter((r) => r.id !== rec.id);
  if (!before.length) return ['Der erste Eintrag in eurer Bibliothek.'];
  const out: string[] = [];
  const wins = before.filter((r) => r.won);
  if (rec.won && !wins.length) out.push('Zum ersten Mal am Ziel!');
  if (rec.days > Math.max(...before.map((r) => r.days))) out.push(`So weit wie noch nie: bis Tag ${rec.days}.`);
  if (rec.fights > Math.max(...before.map((r) => r.fights))) out.push(`So viel gekämpft wie nie: ${rec.fights} Kämpfe.`);
  if (rec.driven > Math.max(...before.map((r) => r.driven))) out.push(`So viele Sturmwesen vertrieben wie nie: ${rec.driven}.`);
  if (rec.won && wins.length && rec.farewells < Math.min(...wins.map((r) => r.farewells))) out.push(rec.farewells === 0 ? 'Zum ersten Mal ohne einen einzigen Abschied am Ziel.' : 'So wenige Abschiede wie nie bei einer Ankunft.');
  const levels = (r: RunRecord) => Math.max(0, ...r.crew.map((c) => c.level));
  if (levels(rec) > Math.max(...before.map(levels))) out.push(`So ein hohes Level hatte noch niemand: ${levels(rec)}.`);
  out.push(`Reise Nummer ${before.length + 1} in eurer Bibliothek${wins.length + (rec.won ? 1 : 0) ? `, ${wins.length + (rec.won ? 1 : 0)}-mal am Ziel` : ''}.`);
  return out;
}

